<script setup>
import { ref, computed, watch } from '#imports';
/* eslint-disable import/no-unresolved */
import groupBy from 'lodash-es/groupBy';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import NewsItem from '~/components/news-page/news-page-item/news-page-item.vue';
import { formatNewsDay } from '~/utils/format-news-date';

const props = defineProps({
  newsData: {
    type: Object,
    required: true,
  },
});

const swiper = ref();
const modules = [Navigation];
const emit = defineEmits(['fetch']);

const isBeginning = computed(
  () => swiper.value && swiper.value.realIndex !== 0
);
const isEnd = computed(() => swiper.value && !swiper.value.isEnd);

function setSwiper(swiperInstance) {
  swiper.value = swiperInstance;
}

const sortByPublishDate = computed(() =>
  groupBy(
    props.newsData.filter((news) => !news.isChronicle),
    (news) => new Date(news.publishedAt).toDateString()
  )
);

const chronicItems = computed(() =>
  props.newsData.filter((news) => news.isChronicle)
);

const updateHeight = () => {
  swiper.value.updateAutoHeight();
};

const handleFetchMoreData = () => {
  emit('fetch');
};

watch(
  () => sortByPublishDate.value,
  () => {
    swiper.value.update();
  }
);
</script>

<template>
  <div class="carousel">
    <template v-if="isBeginning">
      <button
        class="carousel__button carousel__button--prev"
        @click="
          () => {
            swiper.slidePrev();
          }
        "
      ></button>
    </template>
    <NuxtErrorBoundary @error="() => {}">
      <Swiper
        :modules="modules"
        slides-per-view="auto"
        :space-between="12"
        :slides-per-group="1"
        :auto-height="true"
        observer
        observe-parents
        direction="horizontal"
        @reachEnd="handleFetchMoreData"
        @swiper="setSwiper"
        @slideChange="updateHeight"
      >
        <SwiperSlide v-if="chronicItems.length > 0">
          <h2 class="carousel__title">Сейчас</h2>
          <div v-for="news in chronicItems" :key="news._id">
            <NewsItem
              :title="news.title"
              :date="news.publishedAt"
              :slug="news.slug.current"
              :border="news.isChronicle"
            />
          </div>
        </SwiperSlide>
        <SwiperSlide v-for="(newsGroup, date) in sortByPublishDate" :key="date">
          <h2 class="carousel__title">{{ formatNewsDay(date) }}</h2>
          <div class="carousel__slide">
            <div v-for="news in newsGroup" :key="news._id">
              <NewsItem
                :title="news.title"
                :date="news.publishedAt"
                :slug="news.slug.current"
              />
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </NuxtErrorBoundary>
    <template v-if="isEnd">
      <button
        class="carousel__button carousel__button--next"
        @click="
          () => {
            swiper.slideNext();
          }
        "
      ></button>
    </template>
  </div>
</template>

<style scoped lang="postcss">
.carousel {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: baseline;

  &__title {
    text-align: center;
    line-height: 40px;
  }

  &__slide {
    margin: 20px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__button {
    position: absolute;
    z-index: 3;
    top: 0;
    margin: auto;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--accent-color);
    cursor: pointer;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      width: 10px;
      height: 17px;
      background-image: url('~/assets/images/chevron_right.svg');
    }

    &--next {
      left: auto;
      right: 0;
    }

    &--next::after {
      transform: translateX(1px);
    }

    &--prev {
      left: 0;
      right: auto;
    }

    &--prev::after {
      transform: translateX(-1px) rotate(180deg);
    }
  }
}
</style>
